import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { formatWhatsApp } from "@/lib/utils";
import { cliqueNoLinkDoWhatsApp } from "@/lib/abreWhatsApp";
import { MessageCircle } from "lucide-react";

interface Birthday {
  id: string;
  firstName: string | null;
  whatsappNumber: string | null;
  birthDate: string;
  dias: number; // 0 = hoje, positivo = falta, negativo = passou
}

function rotulo(dias: number): string {
  if (dias === 0) return "HOJE!";
  if (dias > 0) return dias === 1 ? "amanhã" : `em ${dias} dias`;
  return dias === -1 ? "ontem" : `há ${-dias} dias`;
}

// Lista de aniversariantes da loja. Dashboard usa past=0 (hoje + próximos N);
// a página Clientes usa past=7 (pega também quem acabou de fazer). O botão
// abre o WhatsApp do cliente com a mensagem pré-preenchida — a campanha é do
// lojista, nada automático vai pro cliente.
const MENSAGEM_PADRAO = "Feliz aniversário, {nome}! 🎉";

export function BirthdaySection({
  next,
  past = 0,
  compact = false,
  message,
}: { next: number; past?: number; compact?: boolean; message?: string | null }) {
  const { data: birthdays = [], isLoading } = useQuery<Birthday[]>({
    queryKey: ["customer-birthdays", next, past],
    queryFn: () => customFetch(`/api/customers/birthdays?next=${next}&past=${past}`),
  });

  if (isLoading || birthdays.length === 0) return null;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2 font-semibold text-sm">🎂 Aniversariantes</div>
      <div className="space-y-2">
        {birthdays.map((b) => {
          const nome = b.firstName || "Cliente";
          const digits = (b.whatsappNumber || "").replace(/\D/g, "");
          // O texto é o que o lojista escreveu em Configurações (o cupom tem
          // mensagem FIXA — foi confusão real: ele editava a da impressão
          // achando que mudava esta).
          const texto = (message?.trim() || MENSAGEM_PADRAO).replaceAll("{nome}", nome);
          const wa = digits
            ? `https://api.whatsapp.com/send?phone=${digits}&text=${encodeURIComponent(texto)}`
            : null;
          return (
            <div key={b.id} className="flex items-center gap-2 text-sm">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${b.dias === 0 ? "bg-pink-100 text-pink-700" : "bg-muted text-muted-foreground"}`}>
                {rotulo(b.dias)}
              </span>
              <span className="truncate font-medium">{nome}</span>
              {!compact && b.whatsappNumber && (
                <span className="text-xs text-muted-foreground truncate hidden sm:inline">{formatWhatsApp(b.whatsappNumber)}</span>
              )}
              {wa && (
                <a
                  href={wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={cliqueNoLinkDoWhatsApp}
                  className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1 hover:bg-green-100"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Parabenizar
                </a>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
